import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { getCampaignModificationRequestDetails } from '../../api/campaign';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdArrowBack } from 'react-icons/md';

const ModificationRequestDetails = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  useEffect(() => {
    setHeaderTitle('Modification Request Details');
    if (id) {
      fetchRequestDetails();
    }
  }, [setHeaderTitle, id]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const data = await getCampaignModificationRequestDetails(id);
      setRequest(data);
    } catch (error) {
      console.error('Error fetching modification request details:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'pending' || statusLower === 'active') {
      return <span className='px-4 py-2 bg-chipYellow text-white rounded-full text-base font-semibold'>Pending</span>;
    } else if (statusLower === 'approved' || statusLower === 'resolved') {
      return <span className='px-4 py-2 bg-chipBlue text-white rounded-full text-base font-semibold'>Resolved</span>;
    } else if (statusLower === 'rejected') {
      return <span className='px-4 py-2 bg-red-500 text-white rounded-full text-base font-semibold'>Rejected</span>;
    }
    return <span className='px-4 py-2 bg-gray-400 text-white rounded-full text-base font-semibold'>{status || 'Pending'}</span>;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className='p-6'>
        <div className='text-center py-20'>
          <p className='text-lg text-gray-500'>Request not found</p>
          <button
            onClick={() => navigate('/campaigns/mod-req')}
            className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark'
          >
            Back to Requests
          </button>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </div>
    );
  }

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <button
          onClick={() => navigate('/campaigns/mod-req')}
          className='flex items-center gap-2 text-primary hover:text-primaryHoverDark mb-4 font-medium'
        >
          <MdArrowBack className='text-xl' />
          Back to Requests
        </button>

        {/* Header */}
        <div className='flex justify-between items-start mb-6 pb-6 border-b border-gray-200'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>Modification Request Details</h1>
            <p className='text-sm text-gray-600'>Submitted on {formatDate(request.createdAt)}</p>
          </div>
          {getStatusBadge(request.status || (request.active ? 'Active' : 'Resolved'))}
        </div>

        {/* Campaign Information */}
        <div className='mb-6'>
          <label className='block text-sm font-medium text-gray-500 mb-2'>Campaign</label>
          <div className='bg-gray-50 rounded-md p-4'>
            <h3 className='text-lg font-semibold text-gray-900'>
              {request.campaign?.title || request.campaign?.campaignTitle || 'N/A'}
            </h3>
            {request.campaign?.campaignType && (
              <p className='text-sm text-gray-600 mt-1'>
                Type: {request.campaign.campaignType}
              </p>
            )}
            {(request.campaign?.startDate || request.campaign?.endDate) && (
              <p className='text-sm text-gray-600 mt-1'>
                Duration: {formatDate(request.campaign.startDate)} - {formatDate(request.campaign.endDate)}
              </p>
            )}
          </div>
        </div>

        {/* Modification Message */}
        <div className='mb-6'>
          <label className='block text-sm font-medium text-gray-500 mb-2'>Requested Modifications</label>
          <div className='bg-gray-50 rounded-md p-4'>
            <p className='text-base text-gray-900 whitespace-pre-wrap'>{request.message || 'N/A'}</p>
          </div>
        </div>

        {/* Uploaded Images */}
        {request.image && (
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-500 mb-3'>
              Uploaded Images {Array.isArray(request.image) && `(${request.image.length})`}
            </label>
            <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
              {Array.isArray(request.image) ? (
                <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                  {request.image.map((img, index) => (
                    <div key={index} className='relative group'>
                      <img
                        src={img}
                        alt={`Attachment ${index + 1}`}
                        className='w-full h-32 object-cover rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity'
                        onClick={() => window.open(img, '_blank')}
                      />
                      <div className='absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1 text-center rounded-b-lg'>
                        Image {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <img
                  src={request.image}
                  alt="Modification Request Attachment"
                  className='max-w-full h-auto rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity'
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                  onClick={() => window.open(request.image, '_blank')}
                />
              )}
              <p className='text-xs text-gray-500 mt-3'>Click image to view full size</p>
            </div>
          </div>
        )}

        {/* Request Metadata */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-gray-200'>
          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Request ID</label>
            <p className='text-base text-gray-900 font-mono'>{request._id || request.id || 'N/A'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Status</label>
            <p className='text-base text-gray-900'>{request.status || (request.active ? 'Active' : 'Resolved')}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Submitted Date</label>
            <p className='text-base text-gray-900'>{formatDate(request.createdAt)}</p>
          </div>

          {request.updatedAt && (
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>Last Updated</label>
              <p className='text-base text-gray-900'>{formatDate(request.updatedAt)}</p>
            </div>
          )}
        </div>

        {/* Admin Response */}
        {request.adminResponse && (
          <div className='pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Admin Response</label>
            <div className='bg-blue-50 border border-blue-200 rounded-md p-4'>
              <p className='text-base text-gray-900 whitespace-pre-wrap'>{request.adminResponse}</p>
              {request.respondedAt && (
                <p className='text-sm text-gray-600 mt-2'>Responded on {formatDate(request.respondedAt)}</p>
              )}
            </div>
          </div>
        )}

        {/* Rejection Reason */}
        {request.rejectionReason && (
          <div className='pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Rejection Reason</label>
            <div className='bg-red-50 border border-red-200 rounded-md p-4'>
              <p className='text-base text-gray-900 whitespace-pre-wrap'>{request.rejectionReason}</p>
            </div>
          </div>
        )}
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default ModificationRequestDetails;
